import { forwardRef, useImperativeHandle, useMemo } from "react";
import { MatchAllTermsProvider as MatchAllTermsProviderBase } from "@/features/match-all-terms";
import { FormLevelFeatureType, useMetadataQuery } from "@/entities/form-metadata";
import { useMatchAllTerms } from "./useMatchAllTerms";

interface MatchAllTermsProps {
    children: React.ReactNode;
}

export interface MatchAllTermsProviderRef {
    matchAllTermsExactly: boolean;
    matchAllTermsSupported: boolean;
}

/**
 * Adapter component that bridges widget-layer form state with features-layer context.
 * Manages "match all terms exactly" functionality by coordinating name and event exactness.
 * Encapsulates all match-all-terms business logic and provides context to child components.
 */
export const MatchAllTermsProvider = forwardRef(({ children }: MatchAllTermsProps, ref) => {
    const { data: formMetadata } = useMetadataQuery();

    const matchAllTermsSupported = useMemo(
        () => formMetadata.features.some((f) => f.featureType === FormLevelFeatureType.MatchAllTerms),
        [formMetadata.features],
    );

    const matchAllTerms = useMatchAllTerms();

    useImperativeHandle(
        ref,
        (): MatchAllTermsProviderRef => ({
            get matchAllTermsExactly() {
                return matchAllTerms.matchAllTermsExactly;
            },
            get matchAllTermsSupported() {
                return matchAllTermsSupported;
            },
        }),
        [matchAllTerms.matchAllTermsExactly, matchAllTermsSupported],
    );

    return (
        <MatchAllTermsProviderBase value={{ ...matchAllTerms, matchAllTermsSupported }}>
            {children}
        </MatchAllTermsProviderBase>
    );
});

MatchAllTermsProvider.displayName = "MatchAllTermsProvider";
